/**
 * Claim flow (self-onboarding) — พนักงานยืนยันด้วย "เลขบัตรประชาชน" → ระบบโชว์ชื่อ
 * ที่ลงทะเบียนไว้ให้กดยืนยัน (ไม่ต้องพิมพ์ชื่อ = กันพิมพ์ผิด) → ถ่าย selfie ยืนยันตัวตน
 * → ผูก LINE → ไปกรอกข้อมูลที่เหลือ. เปิดผ่านลิงก์/QR (?tenant=CODE) ในแอป LINE.
 */
import { useRef, useState } from 'react';
import { ShieldCheck, ArrowRight, ArrowLeft, Camera } from 'lucide-react';
import dayjs from 'dayjs';
import { apiFetch, uploadPairFile } from '../lib/api-client';
import { useAuthStore } from '../lib/store';
import { getIDToken } from '../lib/liff';
import { watermarkImage } from '../lib/watermark';
import type { AuthProfile } from '@payroll/shared';

type Step = 'input' | 'confirm' | 'selfie';

export function Claim() {
  const params = new URLSearchParams(window.location.search);
  const [tenantCode, setTenantCode] = useState((params.get('tenant') ?? '').toUpperCase());
  // วิธียืนยันตัวตน: เบอร์โทร (ค่าเริ่มต้น) หรือ เลขบัตรประชาชน
  const [method, setMethod] = useState<'phone' | 'nid'>('phone');
  const [nid, setNid] = useState('');
  const [phone, setPhone] = useState('');
  const [step, setStep] = useState<Step>('input');
  const [matchedName, setMatchedName] = useState('');
  const [selfiePath, setSelfiePath] = useState<string | null>(null);
  const [selfiePreview, setSelfiePreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const selfieRef = useRef<HTMLInputElement>(null);
  const setAuth = useAuthStore((s) => s.setAuth);

  const cleanNid = nid.replace(/\D/g, '');
  const cleanPhone = phone.replace(/\D/g, '');

  /** ตัวระบุตัวตนที่จะส่งไป backend ตามวิธีที่เลือก (เบอร์ขึ้นต้น 0 / เลขบัตร 13 หลัก) */
  function identifierBody(): { phone: string } | { national_id: string } {
    return method === 'phone' ? { phone: cleanPhone } : { national_id: cleanNid };
  }

  function uploadArgs(): { idToken: string; tenantCode: string } {
    const idToken = getIDToken();
    if (!idToken) throw new Error('ไม่พบ idToken — กรุณาเปิดลิงก์นี้ในแอป LINE');
    return { idToken, tenantCode: tenantCode.toUpperCase() };
  }

  // Step 1 — หาด้วยเลขบัตร → โชว์ชื่อให้ยืนยัน
  async function lookup() {
    if (tenantCode.length < 4) return setError('กรอกรหัสบริษัท');
    if (method === 'phone') {
      if (!/^0\d{8,9}$/.test(cleanPhone)) return setError('กรอกเบอร์โทรให้ถูกต้อง (ขึ้นต้น 0)');
    } else if (cleanNid.length !== 13) {
      return setError('เลขบัตรประชาชนต้องมี 13 หลัก');
    }
    setBusy(true); setError(null);
    try {
      const idToken = getIDToken();
      if (!idToken) throw new Error('ไม่พบ idToken — กรุณาเปิดลิงก์นี้ในแอป LINE');
      const r = await apiFetch<{ display_name: string; already_claimed: boolean }>('/auth/claim/lookup', {
        method: 'POST',
        skipAuth: true,
        body: { tenant_code: tenantCode.toUpperCase(), ...identifierBody(), idToken },
      });
      if (r.already_claimed) throw new Error('บัญชีนี้ถูกเชื่อมไปแล้ว — ถ้าไม่ใช่คุณ ติดต่อ HR');
      setMatchedName(r.display_name);
      setStep('confirm');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'ไม่พบข้อมูล');
    } finally {
      setBusy(false);
    }
  }

  // Step 3 — ถ่าย selfie (บังคับ) → อัปโหลด
  async function handleSelfie(file: File) {
    setBusy(true); setError(null);
    try {
      const stamp = dayjs().format('D MMM YYYY · HH:mm');
      const watermarked = await watermarkImage({
        source: file,
        lines: ['ภาพถ่ายเพื่อยืนยันตัวตน', matchedName, stamp],
      });
      const path = await uploadPairFile(watermarked, uploadArgs(), `claim_selfie_${Date.now()}.jpg`);
      setSelfiePath(path);
      setSelfiePreview(URL.createObjectURL(watermarked));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'อัปโหลดล้มเหลว');
    } finally {
      setBusy(false);
    }
  }

  // Step 4 — ยืนยัน + แนบ selfie → ผูกบัญชี
  async function confirmBind() {
    if (!selfiePath) { setError('กรุณาถ่ายภาพ selfie ก่อน'); return; }
    setBusy(true); setError(null);
    try {
      const idToken = getIDToken();
      if (!idToken) throw new Error('ไม่พบ idToken — กรุณาเปิดลิงก์นี้ในแอป LINE');
      const result = await apiFetch<{ jwt: string; profile: AuthProfile }>('/auth/claim', {
        method: 'POST',
        skipAuth: true,
        body: { tenant_code: tenantCode.toUpperCase(), ...identifierBody(), idToken, selfie_path: selfiePath },
      });
      setAuth(result.jwt, result.profile);
      window.location.href = '/me/profile';
    } catch (e) {
      setError(e instanceof Error ? e.message : 'ยืนยันไม่สำเร็จ');
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card max-w-sm w-full p-6 space-y-3">
        <div className="text-center">
          <ShieldCheck className="w-10 h-10 text-teal mx-auto mb-1" />
          <h2 className="text-xl font-bold">ลงทะเบียนพนักงาน</h2>
          <p className="text-sm text-gray-500 mt-1">
            {step === 'input' ? 'ยืนยันตัวตนด้วยเบอร์โทรหรือเลขบัตรที่บริษัทลงทะเบียนไว้'
              : step === 'confirm' ? 'ตรวจสอบว่าใช่คุณ'
              : 'ถ่ายภาพใบหน้าเพื่อยืนยันตัวตน'}
          </p>
        </div>

        {step === 'input' && (
          <>
            <div>
              <label className="label">รหัสบริษัท</label>
              <input value={tenantCode} onChange={(e) => setTenantCode(e.target.value.toUpperCase())} maxLength={12}
                placeholder="ABCDEF" className="input text-center font-mono tracking-widest" />
            </div>

            {/* เลือกวิธียืนยัน: เบอร์โทร (ค่าเริ่มต้น) / เลขบัตร */}
            <div className="grid grid-cols-2 gap-1 p-1 bg-gray-100 rounded-lg">
              {([['phone', 'เบอร์โทร'], ['nid', 'เลขบัตร']] as const).map(([m, label]) => (
                <button key={m} type="button"
                  onClick={() => { setMethod(m); setError(null); }}
                  className={`py-1.5 rounded-md text-sm font-medium transition ${method === m ? 'bg-white shadow text-teal-700' : 'text-gray-500'}`}>
                  {label}
                </button>
              ))}
            </div>

            {method === 'phone' ? (
              <div>
                <label className="label">เบอร์โทรศัพท์</label>
                <input value={phone} inputMode="numeric" onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="0812345678" className="input text-center font-mono tracking-wider" />
              </div>
            ) : (
              <div>
                <label className="label">เลขบัตรประชาชน (13 หลัก)</label>
                <input value={nid} inputMode="numeric" onChange={(e) => setNid(e.target.value.replace(/\D/g, '').slice(0, 13))}
                  placeholder="1234567890123" className="input text-center font-mono tracking-wider" />
              </div>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button onClick={lookup} disabled={busy} className="btn-primary w-full justify-center">
              {busy ? 'กำลังตรวจสอบ…' : <>ถัดไป <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-xs text-gray-400 text-center">ถ้ายังไม่ได้ลงทะเบียน หรือข้อมูลไม่ตรง — ติดต่อ HR/เจ้าของบริษัท</p>
          </>
        )}

        {step === 'confirm' && (
          <>
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 text-center">
              <p className="text-sm text-gray-500">คุณคือ</p>
              <p className="text-2xl font-bold text-gray-900 my-1">{matchedName}</p>
              <p className="text-sm text-gray-500">ใช่ไหม?</p>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button onClick={() => { setError(null); setStep('selfie'); }} disabled={busy} className="btn-primary w-full justify-center">
              ใช่ — ถัดไป <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => { setStep('input'); setError(null); }} disabled={busy}
              className="btn-secondary w-full justify-center text-sm">
              <ArrowLeft className="w-4 h-4" /> ไม่ใช่ — กลับไปแก้ข้อมูล
            </button>
          </>
        )}

        {step === 'selfie' && (
          <>
            <p className="text-xs text-gray-500 text-center">ภาพจะถูกบันทึกพร้อมชื่อและวันเวลา เพื่อยืนยันตัวตน</p>
            {selfiePreview && <img src={selfiePreview} alt="selfie" className="w-full rounded-lg border" />}
            <button onClick={() => selfieRef.current?.click()} disabled={busy} className="btn-secondary w-full justify-center">
              <Camera className="w-5 h-5" /> {busy ? 'กำลังอัปโหลด…' : selfiePreview ? 'ถ่ายใหม่' : 'ถ่ายรูป'}
            </button>
            <input ref={selfieRef} type="file" accept="image/*" capture="user" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleSelfie(f); e.target.value = ''; }} />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button onClick={confirmBind} disabled={busy || !selfiePath} className="btn-primary w-full justify-center">
              {busy ? 'กำลังเชื่อมบัญชี…' : <>ยืนยัน & เชื่อมบัญชี <ArrowRight className="w-4 h-4" /></>}
            </button>
            <button onClick={() => { setStep('confirm'); setError(null); }} disabled={busy}
              className="btn-secondary w-full justify-center text-sm">
              <ArrowLeft className="w-4 h-4" /> ย้อนกลับ
            </button>
          </>
        )}
      </div>
    </div>
  );
}
